/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   networkService.ts
 *     Feature         :   140-network-topology-dashboard
 *     Description     :   API service and React Query hooks for Network Topology
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-20
 * =========================================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../apiClient';
import type {
  NetworkSummary,
  VNet,
  VNetDetail,
  PrivateEndpoint,
  NSG,
  NsgDetail,
} from '../../types/network';

// --- Query Keys ---
export const OPERATIONS_NETWORK_KEYS = {
  all: ['operations', 'network'] as const,
  summary: () => [...OPERATIONS_NETWORK_KEYS.all, 'summary'] as const,
  vnets: (filters: Record<string, unknown>) => [...OPERATIONS_NETWORK_KEYS.all, 'vnets', filters] as const,
  vnetDetail: (id: string) => [...OPERATIONS_NETWORK_KEYS.all, 'vnets', id, 'detail'] as const,
  privateEndpoints: (filters: Record<string, unknown>) => [...OPERATIONS_NETWORK_KEYS.all, 'private-endpoints', filters] as const,
  nsgs: (filters: Record<string, unknown>) => [...OPERATIONS_NETWORK_KEYS.all, 'nsgs', filters] as const,
  nsgDetail: (id: string) => [...OPERATIONS_NETWORK_KEYS.all, 'nsgs', id, 'detail'] as const,
};

// --- API Functions ---

async function fetchNetworkSummary(): Promise<NetworkSummary> {
  const { data } = await apiClient.get('/api/operations/network/summary');
  // Spec 286 T159 item 15: a response without data is an error, not an empty estate of zeros.
  if (!data?.data) throw new Error('The network summary response carried no data');
  return data.data;
}

async function fetchVnets(filters: Record<string, unknown>): Promise<VNet[]> {
  const { data } = await apiClient.get('/api/operations/network/vnets', { params: filters });
  return data.data ?? [];
}

async function fetchVnetDetail(id: string): Promise<VNetDetail> {
  const { data } = await apiClient.get(`/api/operations/network/vnets/${id}/detail`);
  return data.data ?? { vnet: null, subnets: [], peerings: [] };
}

async function fetchPrivateEndpoints(filters: Record<string, unknown>): Promise<PrivateEndpoint[]> {
  const { data } = await apiClient.get('/api/operations/network/private-endpoints', { params: filters });
  return data.data ?? [];
}

async function fetchNsgs(filters: Record<string, unknown>): Promise<NSG[]> {
  const { data } = await apiClient.get('/api/operations/network/nsgs', { params: filters });
  return data.data ?? [];
}

async function fetchNsgDetail(id: string): Promise<NsgDetail> {
  const { data } = await apiClient.get(`/api/operations/network/nsgs/${id}`);
  return data.data ?? { nsg: null, associatedSubnets: [] };
}

// --- Hooks ---

export function useNetworkSummary() {
  return useQuery({
    queryKey: OPERATIONS_NETWORK_KEYS.summary(),
    queryFn: fetchNetworkSummary,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useVnets(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: OPERATIONS_NETWORK_KEYS.vnets(filters),
    queryFn: () => fetchVnets(filters),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useVnetDetail(id: string | null) {
  return useQuery({
    queryKey: OPERATIONS_NETWORK_KEYS.vnetDetail(id!),
    queryFn: () => fetchVnetDetail(id!),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

export function usePrivateEndpoints(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: OPERATIONS_NETWORK_KEYS.privateEndpoints(filters),
    queryFn: () => fetchPrivateEndpoints(filters),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useNsgs(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: OPERATIONS_NETWORK_KEYS.nsgs(filters),
    queryFn: () => fetchNsgs(filters),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useNsgDetail(id: string | null) {
  return useQuery({
    queryKey: OPERATIONS_NETWORK_KEYS.nsgDetail(id!),
    queryFn: () => fetchNsgDetail(id!),
    enabled: id !== null,
    staleTime: 30_000,
  });
}
